import { Form, Radio, Select } from 'antd';
import { useContext, useMemo } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { Cascada } from 'app/components/commons/Experience/BasicInfo/Cascada/Cascada';
import { FormLineLayout } from 'app/components/commons/Form/FormLineLayout/FormLineLayout';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotels } from 'app/hooks/data/hotels/useHotel';
import { useCountries } from 'app/hooks/data/useCountries';
import { useEmojis } from 'app/hooks/data/useEmojis';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { Experience } from 'app/typings/experiences';
import { ExperienceType } from 'app/typings/experiences';

import { Brand } from '../FormItems/BasicInformations/Brand';
import { Duration } from '../FormItems/BasicInformations/Duration';
import { Location } from '../FormItems/BasicInformations/Location';
import { Time } from '../FormItems/BasicInformations/Time';

const { Option } = Select;
const RULES = [{ required: true, message: 'Required' }];

export const BasicInformations = () => {
  const form = Form.useFormInstance<
    Partial<Experience> & { experienceType: ExperienceType; isLegacy?: boolean }
  >();
  const formValues = Form.useWatch([], form);
  const { mode, isEditMode } = useContext(ViewEditModeContext);
  const { data: hotels } = useHotels();
  const { data: countries } = useCountries();
  const { data: experienceCategories } = useExperiencesCategories();
  const { data: emojis } = useEmojis();

  const isOriginal = formValues?.experienceType === ExperienceType.STAYCATION;

  const hotelOrOriginal = useMemo(() => {
    const isHotel = formValues?.experienceType === ExperienceType.HOTEL;

    return {
      label: isHotel ? 'Hotel' : 'Country',
      name: isHotel ? 'hotelId' : 'countryId',
      options: isHotel ? hotels ?? [] : countries ?? [],
    };
  }, [formValues?.experienceType, hotels, countries]);

  const selectedCategory = useMemo(() => {
    if (!experienceCategories || !formValues?.categoryId) {
      return undefined;
    }

    return experienceCategories.find((cat) => cat.id === formValues.categoryId);
  }, [experienceCategories, formValues?.categoryId]);

  if (!hotels || !experienceCategories || !countries) {
    return null;
  }

  const addEmoji = (categoryId: number) => {
    const category = experienceCategories.find((cat) => cat.id === categoryId);

    if (category) {
      const emoji = emojis?.find((e) => e.id === category.pictureId);

      if (emoji) {
        form.setFieldValue('emoji', emoji);
      }
    }
  };

  return (
    <div>
      <div className="basic-information-container">
        <SCard title="Basic Informations" className="experience-form">
          <FormLineLayout label="Activity type" isRequired>
            <Form.Item name="experienceType" rules={RULES}>
              {isEditMode ? (
                <Radio.Group disabled>
                  <Radio value="HOTEL">Hotel</Radio>
                  <Radio value="STAYCATION">Staycation (Originals)</Radio>
                </Radio.Group>
              ) : (
                <span className="view-data">
                  {formValues &&
                  formValues.experienceType === ExperienceType.HOTEL
                    ? 'Hotel'
                    : 'Staycation (Originals)'}
                </span>
              )}
            </Form.Item>
          </FormLineLayout>

          <FormLineLayout label={hotelOrOriginal.label} isRequired>
            <Form.Item name={hotelOrOriginal.name} rules={RULES}>
              {isEditMode ? (
                <Select
                  placeholder={`Select a ${hotelOrOriginal.label.toLowerCase()}`}
                  showSearch
                  optionFilterProp="children"
                  disabled={!isOriginal}
                >
                  {hotelOrOriginal.options.map((option) => (
                    <Option key={option.id} value={option.id}>
                      {option.name}
                    </Option>
                  ))}
                </Select>
              ) : (
                <span className="view-data">
                  {formValues && formValues.hotelId
                    ? hotels.find((h) => h.id === formValues['hotelId'])?.name
                    : (formValues &&
                        formValues.countryId &&
                        countries.find((c) => c.id === formValues['countryId'])
                          ?.name) ||
                      'ø empty'}
                </span>
              )}
            </Form.Item>
          </FormLineLayout>

          <FormLineLayout label="Category" isRequired disabled={!isEditMode}>
            <Form.Item name="categoryId" rules={RULES}>
              <Cascada
                dataSource={experienceCategories}
                onChange={addEmoji}
                mode={mode}
              />
            </Form.Item>
            <Form.Item name="emoji" hidden>
              <input type="hidden" />
            </Form.Item>
          </FormLineLayout>

          {selectedCategory && (
            <>
              {!!selectedCategory.brand && <Brand />}
              {!!selectedCategory.duration && <Duration />}
              {!!selectedCategory.time && <Time />}
              {!!selectedCategory.locations && <Location />}
            </>
          )}

          <Form.Item name="emoji" hidden>
            <input type="hidden" />
          </Form.Item>
        </SCard>
      </div>
    </div>
  );
};
